// Ícones outline (stroke, não emoji) — extraídos por amostragem direta da imagem de referência
// que o usuário passou (13/08): a referência usa uma família de ícones de traço fino e cantos
// arredondados (estilo Lucide/Feather), não emoji do sistema. Centralizados aqui pra reusar entre
// VistoriaDetalhe, NavInferior e Layout sem duplicar path a cada uso.
import type { CSSProperties } from "react";

interface PropsIcone {
  tamanho?: number;
  cor?: string;
  style?: CSSProperties;
}

const basePropsSvg = (tamanho: number, cor: string) => ({
  width: tamanho,
  height: tamanho,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  stroke: cor,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

/** Círculo com check — vazio (contorno) quando `selecionado` é false, preenchido com check branco
 * quando true. Usado no segmentado Conforme/Não conforme/N.A. e no selo "Conformes" do resumo. */
export function IconeCirculoCheck({
  tamanho = 20,
  cor = "currentColor",
  selecionado = false,
  style,
}: PropsIcone & { selecionado?: boolean }) {
  if (selecionado) {
    return (
      <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" style={style}>
        <circle cx="12" cy="12" r="12" fill={cor} />
        <path d="M7 12.5l3.2 3.2L17 9" stroke="white" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" style={style}>
      <circle cx="12" cy="12" r="10" stroke={cor} strokeWidth={1.6} fill="none" />
    </svg>
  );
}

export function IconeAvisoTriangulo({ tamanho = 20, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M12 4.5 21.5 20h-19L12 4.5Z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="17.2" r="0.15" fill={cor} stroke={cor} strokeWidth={1.4} />
    </svg>
  );
}

export function IconeInterrogacaoCirculo({ tamanho = 20, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M9.3 9.6a2.7 2.7 0 1 1 3.9 2.4c-.9.5-1.2 1-1.2 1.9" />
      <circle cx="12" cy="17" r="0.15" fill={cor} stroke={cor} strokeWidth={1.4} />
    </svg>
  );
}

export function IconeInfo({ tamanho = 16, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v5.5" />
      <circle cx="12" cy="7.6" r="0.15" fill={cor} stroke={cor} strokeWidth={1.4} />
    </svg>
  );
}

export function IconeLixeira({ tamanho = 18, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 12.5A2 2 0 0 0 9 21.5h6a2 2 0 0 0 2-2L18 7" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

export function IconeMais({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

export function IconeCamera({ tamanho = 24, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-1.6A1.5 1.5 0 0 1 9.8 4.7h4.4a1.5 1.5 0 0 1 1.3.7L16.5 7h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  );
}

export function IconeFechar({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

export function IconeDownload({ tamanho = 20, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M12 4v11.5" />
      <path d="M7.5 11 12 15.5 16.5 11" />
      <path d="M4.5 18.5h15" />
    </svg>
  );
}

export function IconeSetaEsquerda({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M19 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </svg>
  );
}

export function IconePontos({ tamanho = 20, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill={cor} style={style}>
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

export function IconeCasa({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <path d="M4 11.5 12 4l8 7.5" />
      <path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}

export function IconePrancheta({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <rect x="5" y="4.5" width="14" height="17" rx="2" />
      <path d="M9 4.5V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v.5" />
      <path d="M8.5 12.5 11 15l4.5-5" />
    </svg>
  );
}

export function IconePredio({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <rect x="5" y="3" width="10" height="18" rx="1" />
      <path d="M15 8h4v13h-4" />
      <path d="M8 7h1" />
      <path d="M11 7h1" />
      <path d="M8 11h1" />
      <path d="M11 11h1" />
      <path d="M8 15h1" />
      <path d="M11 15h1" />
    </svg>
  );
}

export function IconeCalendario({ tamanho = 22, cor = "currentColor", style }: PropsIcone) {
  return (
    <svg {...basePropsSvg(tamanho, cor)} style={style}>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
    </svg>
  );
}
